import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Lock, CheckCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenError, setTokenError] = useState(!token);

  useEffect(() => {
    if (!token) setTokenError(true);
  }, [token]);

  const validatePassword = (p) => {
    if (p.length < 8) return language === 'fr' ? 'Au moins 8 caractères' : 'At least 8 characters';
    if (!/[A-Z]/.test(p)) return language === 'fr' ? 'Au moins une majuscule' : 'At least one uppercase';
    if (!/[a-z]/.test(p)) return language === 'fr' ? 'Au moins une minuscule' : 'At least one lowercase';
    if (!/\d/.test(p)) return language === 'fr' ? 'Au moins un chiffre' : 'At least one digit';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validatePassword(password);
    if (err) { toast.error(err); return; }
    if (password !== confirm) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/auth'), 3000);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (language === 'fr' ? 'Lien invalide ou expiré' : 'Invalid or expired link');
      toast.error(message);
      if (error?.response?.status === 400) setTokenError(true);
    } finally {
      setLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-secondary/30">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="font-heading text-2xl">
              {language === 'fr' ? 'Lien invalide' : 'Invalid link'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Ce lien de réinitialisation est invalide ou a expiré.'
                : 'This reset link is invalid or has expired.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/forgot-password">
              <Button className="w-full">
                {language === 'fr' ? 'Demander un nouveau lien' : 'Request a new link'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-secondary/30">
        <Card className="w-full max-w-md" data-testid="reset-success-card">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="font-heading text-2xl">
              {language === 'fr' ? 'Mot de passe modifié !' : 'Password updated!'}
            </CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Redirection vers la connexion...'
                : 'Redirecting to login...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/auth">
              <Button className="w-full">
                {language === 'fr' ? 'Se connecter maintenant' : 'Login now'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-secondary/30">
      <Card className="w-full max-w-md" data-testid="reset-password-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-2xl">R</span>
          </div>
          <CardTitle className="font-heading text-2xl">
            {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
          </CardTitle>
          <CardDescription>
            {language === 'fr'
              ? 'Choisissez un mot de passe fort et confidentiel.'
              : 'Choose a strong, confidential password.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">
                {language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                  data-testid="reset-password-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {language === 'fr' ? '8+ caractères, 1 majuscule, 1 minuscule, 1 chiffre' : '8+ chars, 1 uppercase, 1 lowercase, 1 digit'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">
                {language === 'fr' ? 'Confirmer' : 'Confirm'}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="confirm"
                  type="password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-10"
                  required
                  minLength={8}
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>

            <Button type="submit" className="w-full h-12" disabled={loading} data-testid="reset-submit-btn">
              {loading && <Loader2 className="h-5 w-5 mr-2 animate-spin" />}
              {language === 'fr' ? 'Réinitialiser' : 'Reset'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
